import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  useTheme,
  alpha,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import {
  MDRData,
  WorkPackage,
  WorkUnit,
  DocumentType,
  Discipline,
  Party,
  User,
  Project,
  RevisionName,
  DocumentRevision,
} from '../types';
import projectsService from '../services/projectsService';
import CollapsiblePanel from './CollapsiblePanel';
import DocumentForm from './forms/DocumentForm';
import RevisionForm from './forms/RevisionForm';
import RevisionWorkflowForm from './forms/RevisionWorkflowForm';
import AuxiliaryFilesWidget, { AuxFileDraft } from './forms/AuxiliaryFilesWidget';
import {
  DocumentFormValues,
  RevisionFormValues,
  RevisionWorkflowFormValues,
  buildDefaultDocumentFormValues,
  buildDefaultRevisionFormValues,
  buildDefaultRevisionWorkflowFormValues,
  isDocumentFormValid,
  isRevisionFormValid,
  isRevisionWorkflowFormValid,
  getDocumentFormMissingFields,
  getRevisionFormMissingFields,
  getRevisionWorkflowFormMissingFields,
  labelsForDocumentFields,
  hasWorkflowParties,
} from './forms/formTypes';
import { useFormFieldErrors } from '../hooks/useFormFieldErrors';
import FormValidationAlert from './FormValidationAlert';
import { buildCreateRevisionPayload, uploadAuxDraftsForRevision } from './forms/workflowUtils';
import {
  hasStartedRevision,
  WORKFLOW_ASSIGNMENT_CONFLICT_ERROR,
} from '../utils/revisionUtils';

const NAUTILUS = { deepOcean: '#0f2134', sand: '#ccba9b' };

export interface UnifiedDocumentDialogProps {
  open: boolean;
  onClose: () => void;
  mode: 'create' | 'edit';
  document: MDRData | null;
  projectId: number;
  workPackages: WorkPackage[];
  workUnits: WorkUnit[];
  documentTypes: DocumentType[];
  disciplines: Discipline[];
  projectUsers: User[];
  currentProject: Project | null;
  parties: Party[];
  revisionNames: RevisionName[];
  onLoadWorkUnits: (workPackageId?: string) => void;
  onSuccess: () => void;
  onError: (message: string) => void;
}

const UnifiedDocumentDialog: React.FC<UnifiedDocumentDialogProps> = ({
  open,
  onClose,
  mode,
  document,
  projectId,
  workPackages,
  workUnits,
  documentTypes,
  disciplines,
  projectUsers,
  currentProject,
  parties,
  revisionNames,
  onLoadWorkUnits,
  onSuccess,
  onError,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const panelBorder = isDark ? '#2c3e50' : '#e0e0e0';
  const surfaceBg = isDark ? NAUTILUS.deepOcean : '#ffffff';

  const [documentForm, setDocumentForm] = useState<DocumentFormValues>(buildDefaultDocumentFormValues());
  const [revisionForm, setRevisionForm] = useState<RevisionFormValues>(buildDefaultRevisionFormValues());
  const [workflowForm, setWorkflowForm] = useState<RevisionWorkflowFormValues>(
    buildDefaultRevisionWorkflowFormValues()
  );
  const [revisionEnabled, setRevisionEnabled] = useState(false);
  const [workflowEnabled, setWorkflowEnabled] = useState(false);
  const [ndNumberAuto, setNdNumberAuto] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [partialError, setPartialError] = useState<string | null>(null);
  const [latestRevision, setLatestRevision] = useState<DocumentRevision | null>(null);
  const [allRevisions, setAllRevisions] = useState<DocumentRevision[]>([]);
  const [loadingRevision, setLoadingRevision] = useState(false);
  const fieldErrors = useFormFieldErrors();
  const [auxNewEntries, setAuxNewEntries] = useState<AuxFileDraft[]>([]);

  const anotherRevisionStarted = useMemo(
    () => hasStartedRevision(allRevisions),
    [allRevisions]
  );

  const isEditCreateRevisionFlow = mode === 'edit' && revisionEnabled;

  const nextRevisionCounter = useMemo(() => {
    if (isEditCreateRevisionFlow || mode === 'create') {
      if (allRevisions.length > 0) {
        return Math.max(...allRevisions.map((r) => r.revision_counter ?? 0)) + 1;
      }
      if (document?.revisions?.length) {
        return Math.max(...document.revisions.map((r) => r.revision_counter ?? 0)) + 1;
      }
      return 0;
    }
    if (mode === 'edit' && latestRevision) {
      return latestRevision.revision_counter ?? 0;
    }
    return 0;
  }, [mode, isEditCreateRevisionFlow, allRevisions, document?.revisions, latestRevision]);

  const wasOpenRef = useRef(false);
  const workUnitSyncedDocIdRef = useRef<number | null>(null);

  const buildDocumentFormFromMdr = useCallback(
    (doc: MDRData, units: WorkUnit[]): DocumentFormValues => {
      const wp = workPackages.find((w) => w.code === doc.work_package_code);
      const dt = documentTypes.find((t) => t.name === doc.document_type_name);
      const wu = units.find((u) => u.code === doc.work_unit_code);
      return {
        nd_number: doc.document_nd_number || '',
        em_number: doc.document_em_number || '',
        title: doc.document_title || '',
        work_package_id: wp?.id.toString() || '',
        work_unit_id: wu?.id.toString() || '',
        document_type_id: dt?.id.toString() || '',
        discipline_id: doc.discipline_id?.toString() || '',
        notes: doc.notes || '',
        target_value: doc.target_value?.toString() || '',
        qualifier: doc.qualifier || '',
        communication_user_ids: doc.communication_user_ids || [],
        communication_include_project_members: doc.communication_include_project_members ?? false,
      };
    },
    [workPackages, documentTypes]
  );

  // Initialize only when the dialog opens (not on every parent re-render).
  useEffect(() => {
    if (!open) {
      wasOpenRef.current = false;
      workUnitSyncedDocIdRef.current = null;
      return;
    }

    const justOpened = !wasOpenRef.current;
    wasOpenRef.current = true;
    if (!justOpened) {
      return;
    }

    setDocumentForm(buildDefaultDocumentFormValues());
    setRevisionForm(buildDefaultRevisionFormValues());
    setWorkflowForm(buildDefaultRevisionWorkflowFormValues());
    setRevisionEnabled(false);
    setWorkflowEnabled(false);
    setNdNumberAuto(true);
    setPartialError(null);
    setLatestRevision(null);
    setAllRevisions([]);
    setLoadingRevision(false);
    setAuxNewEntries([]);
    workUnitSyncedDocIdRef.current = null;

    if (mode === 'edit' && document) {
      const wp = workPackages.find((w) => w.code === document.work_package_code);
      setDocumentForm(buildDocumentFormFromMdr(document, workUnits));
      if (wp?.id) {
        onLoadWorkUnits(wp.id.toString());
      }

      const loadLatestRevision = async () => {
        setLoadingRevision(true);
        try {
          const revisions = await projectsService.getDocumentRevisions(
            projectId,
            document.document_id
          );
          setAllRevisions(revisions);
          if (revisions.length === 0) {
            setLatestRevision(null);
            return;
          }
          const latest = [...revisions].sort(
            (a, b) => (b.revision_counter ?? 0) - (a.revision_counter ?? 0)
          )[0];
          setLatestRevision(latest);
        } catch (err) {
          console.error('Failed to load latest revision', err);
        } finally {
          setLoadingRevision(false);
        }
      };

      void loadLatestRevision();
    }
  }, [
    open,
    mode,
    document,
    projectId,
    workPackages,
    workUnits,
    buildDocumentFormFromMdr,
    onLoadWorkUnits,
  ]);

  // After work units load for edit mode, set work unit once per document.
  useEffect(() => {
    if (!open || mode !== 'edit' || !document || workUnits.length === 0) {
      return;
    }
    if (workUnitSyncedDocIdRef.current === document.document_id) {
      return;
    }
    const wu = workUnits.find((u) => u.code === document.work_unit_code);
    if (!wu) {
      return;
    }
    workUnitSyncedDocIdRef.current = document.document_id;
    setDocumentForm((prev) => {
      if (prev.work_unit_id === wu.id.toString()) {
        return prev;
      }
      return { ...prev, work_unit_id: wu.id.toString() };
    });
  }, [open, mode, document, workUnits]);

  useEffect(() => {
    if (!revisionEnabled) {
      setWorkflowEnabled(false);
    }
  }, [revisionEnabled]);

  // When starting a new-revision flow in edit mode, use empty revision/workflow forms.
  useEffect(() => {
    if (!revisionEnabled || !isEditCreateRevisionFlow) {
      return;
    }
    setRevisionForm(buildDefaultRevisionFormValues());
    setWorkflowForm(buildDefaultRevisionWorkflowFormValues());
  }, [revisionEnabled, isEditCreateRevisionFlow]);

  const selectedWorkPackage = useMemo(
    () => workPackages.find((wp) => wp.id.toString() === documentForm.work_package_id),
    [workPackages, documentForm.work_package_id]
  );

  const workUnitRequired = Boolean(
    documentForm.work_package_id && (selectedWorkPackage?.work_unit_count ?? 0) > 0
  );

  const documentValid = isDocumentFormValid(documentForm, { workUnitRequired });
  const revisionValid = !revisionEnabled || isRevisionFormValid(revisionForm);
  const workflowValid = !workflowEnabled || isRevisionWorkflowFormValid(workflowForm);

  const submitLabel = useMemo(() => {
    if (mode === 'edit') {
      if (isEditCreateRevisionFlow && workflowEnabled && hasWorkflowParties(workflowForm)) {
        return 'Save Document + New Revision + Workflow';
      }
      if (isEditCreateRevisionFlow) {
        return 'Save Document + New Revision';
      }
      return 'Save Changes';
    }
    if (revisionEnabled && workflowEnabled && hasWorkflowParties(workflowForm)) {
      return 'Create Document + Revision + Workflow';
    }
    if (revisionEnabled) {
      return 'Create Document + Revision';
    }
    return 'Create Document';
  }, [mode, revisionEnabled, workflowEnabled, workflowForm, isEditCreateRevisionFlow]);

  const handleSubmit = async () => {
    const missing = [
      ...getDocumentFormMissingFields(documentForm, { workUnitRequired }),
      ...(revisionEnabled ? getRevisionFormMissingFields(revisionForm) : []),
      ...(workflowEnabled ? getRevisionWorkflowFormMissingFields(workflowForm) : []),
    ];
    if (missing.length > 0) {
      fieldErrors.showMissing(missing, labelsForDocumentFields(missing));
      return;
    }
    setSubmitting(true);
    setPartialError(null);
    fieldErrors.clear();

    try {
      if (mode === 'create') {
        const docType = documentTypes.find(
          (dt) => dt.id.toString() === documentForm.document_type_id
        );
        const formData = new FormData();
        formData.append('nd_number', documentForm.nd_number);
        formData.append('em_number', documentForm.em_number);
        formData.append('title', documentForm.title);
        formData.append('work_package_id', documentForm.work_package_id);
        if (documentForm.work_unit_id) {
          formData.append('work_unit_id', documentForm.work_unit_id);
        }
        formData.append('document_type', docType?.name || documentForm.document_type_id);
        formData.append('notes', documentForm.notes);
        formData.append('target_value', documentForm.target_value);
        formData.append('qualifier', documentForm.qualifier);
        formData.append('discipline_id', documentForm.discipline_id);
        if (documentForm.communication_user_ids.length > 0) {
          formData.append(
            'communication_user_ids',
            JSON.stringify(documentForm.communication_user_ids)
          );
        }
        formData.append(
          'communication_include_project_members',
          documentForm.communication_include_project_members ? 'true' : 'false'
        );
        const uploadResult = await projectsService.uploadDocument(projectId, formData);

        if (revisionEnabled) {
          try {
            const payload = buildCreateRevisionPayload(
              uploadResult.document_id,
              revisionForm,
              workflowEnabled ? workflowForm : buildDefaultRevisionWorkflowFormValues()
            );
            const revResult = await projectsService.createDocumentRevision(
              projectId,
              uploadResult.document_id,
              payload
            );
            if (auxNewEntries.length > 0) {
              try {
                await uploadAuxDraftsForRevision(
                  projectId,
                  uploadResult.document_id,
                  revResult.revision_id,
                  auxNewEntries
                );
              } catch (auxErr: any) {
                const auxMsg =
                  auxErr.response?.data?.error ||
                  auxErr.message ||
                  'Failed to upload auxiliary files';
                setPartialError(`Document and revision created. ${auxMsg}`);
                onError(`Document and revision created. ${auxMsg}`);
                return;
              }
            }
          } catch (revErr: any) {
            const msg =
              revErr.response?.data?.error ||
              revErr.message ||
              'Failed to create revision';
            setPartialError(`Document created. Failed to create revision: ${msg}`);
            onError(`Document created. Failed to create revision: ${msg}`);
            return;
          }
        }

        onSuccess();
        onClose();
        return;
      }

      if (!document) return;

      await projectsService.updateDocument(projectId, document.document_id, {
        title: documentForm.title,
        nd_number: documentForm.nd_number,
        em_number: documentForm.em_number,
        work_package_id: documentForm.work_package_id,
        work_unit_id: workUnitRequired ? documentForm.work_unit_id || null : null,
        document_type_id: documentForm.document_type_id,
        discipline_id: documentForm.discipline_id,
        notes: documentForm.notes,
        target_value: documentForm.target_value,
        qualifier: documentForm.qualifier,
        communication_user_ids: documentForm.communication_user_ids,
        communication_include_project_members: documentForm.communication_include_project_members,
      });

      if (revisionEnabled && isEditCreateRevisionFlow) {
        if (workflowEnabled && hasWorkflowParties(workflowForm) && anotherRevisionStarted) {
          onError(WORKFLOW_ASSIGNMENT_CONFLICT_ERROR);
          return;
        }

        const revisionPayload = buildCreateRevisionPayload(
          document.document_id,
          revisionForm,
          workflowEnabled ? workflowForm : buildDefaultRevisionWorkflowFormValues()
        );
        const revResult = await projectsService.createDocumentRevision(
          projectId,
          document.document_id,
          revisionPayload
        );
        if (auxNewEntries.length > 0) {
          try {
            await uploadAuxDraftsForRevision(
              projectId,
              document.document_id,
              revResult.revision_id,
              auxNewEntries
            );
          } catch (auxErr: any) {
            const auxMsg =
              auxErr.response?.data?.error ||
              auxErr.message ||
              'Failed to upload auxiliary files';
            setPartialError(`Document saved and revision created. ${auxMsg}`);
            onError(`Document saved and revision created. ${auxMsg}`);
            return;
          }
        }
      }

      onSuccess();
      onClose();
    } catch (err: any) {
      const msg = err.response?.data?.error || err.message || 'Operation failed';
      onError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const metadataLocked = document?.metadata_edit_locked === true;
  const dialogTitle =
    mode === 'create'
      ? 'Create Document'
      : `Edit Document — ${document?.document_nd_number || ''}`;
  const dialogSubtitle =
    mode === 'edit' ? document?.document_title : undefined;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: surfaceBg,
          border: `1px solid ${panelBorder}`,
          borderRadius: 1,
        },
      }}
    >
      <DialogTitle
        sx={{
          borderBottom: `1px solid ${panelBorder}`,
          py: 1.5,
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                letterSpacing: '-0.01em',
                color: isDark ? '#ffffff' : NAUTILUS.deepOcean,
              }}
            >
              {dialogTitle}
            </Typography>
            {dialogSubtitle && (
              <Typography
                variant="body2"
                sx={{
                  color: isDark ? '#b0bec5' : '#546e7a',
                  fontFamily: 'monospace',
                }}
              >
                {dialogSubtitle}
              </Typography>
            )}
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <FormValidationAlert message={fieldErrors.message} onClose={fieldErrors.clear} />

      <DialogContent sx={{ pt: 2 }}>
        {partialError && (
          <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setPartialError(null)}>
            {partialError}
          </Alert>
        )}

        <CollapsiblePanel title="Document" required enabled={true}>
          <DocumentForm
            value={documentForm}
            onChange={setDocumentForm}
            mode={mode}
            projectId={projectId}
            workPackages={workPackages}
            workUnits={workUnits}
            documentTypes={documentTypes}
            disciplines={disciplines}
            projectUsers={projectUsers}
            currentProject={currentProject}
            onLoadWorkUnits={onLoadWorkUnits}
            ndNumberAutoEnabled={ndNumberAuto}
            onNdNumberManualEdit={() => setNdNumberAuto(false)}
            workUnitRequired={workUnitRequired}
            invalid={fieldErrors.invalid}
            onFieldEdit={fieldErrors.clearField}
          />
        </CollapsiblePanel>

        <CollapsiblePanel
          title={mode === 'edit' ? 'New Revision' : 'Initial Revision'}
          subtitle={
            mode === 'edit'
              ? allRevisions.length === 0
                ? 'Create the first revision for this document'
                : 'Add another revision record for this document'
              : 'Optionally create the first revision with the document'
          }
          enabled={revisionEnabled}
          onEnabledChange={setRevisionEnabled}
          disabled={metadataLocked || (mode === 'edit' && loadingRevision)}
          invalid={revisionEnabled && !revisionValid}
        >
          {loadingRevision ? (
            <Box display="flex" justifyContent="center" py={2}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <>
              <RevisionForm
                value={revisionForm}
                onChange={setRevisionForm}
                revisionNames={revisionNames}
                nextRevisionCounter={nextRevisionCounter}
                lockIdentity={false}
                lockDates={false}
                invalid={fieldErrors.invalid}
                onFieldEdit={fieldErrors.clearField}
              />
              <AuxiliaryFilesWidget
                newEntries={auxNewEntries}
                onNewEntriesChange={setAuxNewEntries}
                disabled={metadataLocked || submitting}
              />
            </>
          )}
        </CollapsiblePanel>

        {mode === 'edit' && revisionEnabled && anotherRevisionStarted && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Another revision already has a workflow assigned. You can create the revision now and
            assign its workflow once the active one is completed or cleared.
          </Alert>
        )}

        <CollapsiblePanel
          title="Revision Workflow"
          subtitle="Originator, checkers, and approvers"
          enabled={workflowEnabled}
          onEnabledChange={setWorkflowEnabled}
          disabled={
            !revisionEnabled ||
            metadataLocked ||
            (mode === 'edit' && anotherRevisionStarted)
          }
          invalid={workflowEnabled && !workflowValid}
        >
          <RevisionWorkflowForm
            value={workflowForm}
            onChange={setWorkflowForm}
            parties={parties}
            invalid={fieldErrors.invalid}
            onFieldEdit={fieldErrors.clearField}
          />
        </CollapsiblePanel>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${panelBorder}` }}>
        <Button onClick={onClose} sx={{ color: 'text.secondary' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          sx={{
            bgcolor: NAUTILUS.sand,
            color: NAUTILUS.deepOcean,
            fontWeight: 600,
            '&:hover': { bgcolor: alpha(NAUTILUS.sand, 0.85) },
            '&:disabled': { bgcolor: alpha(NAUTILUS.sand, 0.3) },
          }}
        >
          {submitting ? (
            <CircularProgress size={20} sx={{ color: NAUTILUS.deepOcean }} />
          ) : (
            submitLabel
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UnifiedDocumentDialog;
